import { useTranslation } from 'react-i18next';
import { ListPlaceholder } from '@/common/components/ListPlaceholder/ListPlaceholder';
import { OperationDetail } from '@/common/components/OperationDetail/OperationDetail';
import { useOperationAction } from '@/common/components/OperationDetail/OperationDetail.utils';
import {
  TTransactionListItem,
  TransactionsList,
  TransactionsListGroup,
  TransactionsListSkeleton,
} from '@/common/components/TransactionsList/TransactionsList';
import { IOperationItem } from '@/common/integrations/menhir/employee/operations/operations.types';
import { useItemProps } from './OperationsList.utils';

export type TOperationsListProps = {
  operations: IOperationItem[];
  loading?: boolean;
  placeholderLength?: number;
  grouped?: boolean;
};

export function OperationsList({
  operations,
  loading,
  placeholderLength = 5,
  grouped,
}: TOperationsListProps) {
  const { t } = useTranslation();

  if (loading) {
    return (
      <TransactionsListSkeleton
        numItems={placeholderLength}
        grouped={grouped}
      />
    );
  }

  return (
    <>
      {operations.length > 0 ? (
        <OperationsListItems operations={operations} grouped={grouped} />
      ) : (
        <ListPlaceholder
          title={t('wallet.activity.empty_placeholder.title')}
          description={t('wallet.activity.empty_placeholder.description')}
        />
      )}
      <OperationDetail />
    </>
  );
}

function OperationsListItems({
  operations,
  grouped,
}: {
  operations: IOperationItem[];
  grouped?: boolean;
}) {
  const { getTransactionAction } = useOperationAction();
  const { getItemProps } = useItemProps();

  const items: TTransactionListItem[] = operations.map((operation) => {
    const itemProps = getItemProps(operation);
    return {
      icon: itemProps.icon,
      date: operation.executedAt,
      description: itemProps.description,
      statusBadge: itemProps.badge,
      amount: operation.amount,
      isDebit: operation.isDebit,
      disabled: ['rejected', 'cancelled'].includes(operation.status),
      action: getTransactionAction(operation),
    };
  });

  if (grouped) {
    return <TransactionsListGroup transactions={items} />;
  }

  return <TransactionsList transactions={items} />;
}
